/**
 * Onboarding · Step 4 — Select the brief.
 *
 * The user clicks the AeroCarry brief node to select it. Shows a pulsing
 * rust halo around the PDF node and a `0 of 1 selected → 1 of 1 selected`
 * counter. Advances when the brief appears in the canvas selection cache.
 *
 * Uses `data.pdfNodeId` set by DragBriefStep. If that's missing (reload /
 * worked-in canvas), falls through immediately so the user isn't stranded.
 */

import { memo, useEffect, useRef } from "react";
import { useCanvasOnboardingUpdate } from "../../../../context/CanvasOnboardingContext";
import { useCanvasSelectionMirror } from "../../../../context/CanvasSelectionMirrorContext";
import { MarqueeSelectDemo } from "../shared/MarqueeSelectDemo";
import { Flourish, StepHeader } from "../shared/StepHeader";
import type { StepProps } from "../shared/types";

/** Delay after selecting the brief before advancing. */
const POST_SELECT_READ_MS = 600;

export const SelectBriefStep = memo(function SelectBriefStep({
  data,
  onAdvance,
}: StepProps) {
  const update = useCanvasOnboardingUpdate();

  const onAdvanceRef = useRef(onAdvance);
  onAdvanceRef.current = onAdvance;

  const pdfNodeId = typeof data.pdfNodeId === "string" ? data.pdfNodeId : null;

  const selectedNodes = useCanvasSelectionMirror();

  const isSelected = pdfNodeId
    ? selectedNodes.some((n) => n.id === pdfNodeId)
    : false;

  // Defensive: if no pdfNodeId was persisted, skip immediately.
  const sealedEmpty = !pdfNodeId;

  // No viewport lock — user needs to click on the canvas.
  // No spotlight — the FileNodeHighlight below halos the specific node.
  useEffect(() => {
    update({ lockedViewport: false, highlightTargetId: null });
    return () => {
      update({ lockedViewport: false, highlightTargetId: null });
    };
  }, [update]);

  useEffect(() => {
    if (!isSelected && !sealedEmpty) return;
    const t = window.setTimeout(
      () => onAdvanceRef.current(),
      sealedEmpty ? 0 : POST_SELECT_READ_MS,
    );
    return () => window.clearTimeout(t);
  }, [isSelected, sealedEmpty]);

  const selectedCount = isSelected ? 1 : 0;

  return (
    <>
      {/* Looping marquee-drag demo around the brief — teaches the
          drag-to-select gesture and points the user at the file. */}
      {pdfNodeId && !isSelected && <MarqueeSelectDemo nodeIds={[pdfNodeId]} />}

      <StepHeader
        heading={
          <>
            The agent sees your <Flourish>selection</Flourish>.
          </>
        }
        lede={
          <>
            Try clicking the brief you just added. Whatever you select is what
            the agent works with — you can drag to select multiple nodes, too.
          </>
        }
      />

      {/* N of 1 counter pill */}
      <div
        className="_co_onb_settle_3"
        style={{
          marginTop: "var(--co-onb-stack-md)",
          display: "inline-flex",
          alignItems: "center",
          gap: 10,
          padding: "8px 14px",
          borderRadius: 999,
          background: isSelected
            ? "var(--co-rust-soft, rgba(176, 87, 49, 0.12))"
            : "var(--co-snap-fill)",
          border: `1px solid ${isSelected ? "var(--co-rust)" : "var(--co-track-strong)"}`,
          fontSize: 13,
          fontWeight: 600,
          letterSpacing: "-0.005em",
          color: isSelected ? "var(--co-rust)" : "var(--co-fg)",
          fontFamily: "inherit",
          transition:
            "color 320ms ease, background-color 320ms ease, border-color 320ms ease",
        }}
      >
        <span
          className={isSelected ? undefined : "_co_onb_dot"}
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: "var(--co-rust)",
            display: "inline-block",
            flexShrink: 0,
          }}
        />
        <span>
          {isSelected
            ? "Click the file to select it"
            : `Click the file to select it · ${selectedCount} of 1 selected`}
        </span>
      </div>
    </>
  );
});
